import React, { useEffect, useRef, useState } from 'react';
import { FormControl, makeStyles, MenuItem, Select } from '@material-ui/core';
import classNames from 'classnames';

import lang from '../../../../../lib/language';
import { RatiosPeriodType } from '../../../../../lib/models/fundamental/types';

import iconExpandUp from '../../../../../assets/img/icon_expan_down_ratios.svg';
import { DEFAULT_FONT_FAMILY } from '../../../../../constants';
import { useBrowserWidth } from '../../../../../hooks';
import { ZOOM_BEHAVIOR_WIDTH } from '../../../../../util/constants';

import styles from './FilterDropDown.module.scss';

type StyleProps = {
  width: number;
  isActive: boolean;
};

const useStyles = makeStyles(() => ({
  select: {
    boxShadow: 'none',
    borderRadius: '0px 2px 2px 0px',
    padding: '7px 0px',
    fontSize: '16px',
    fontFamily: DEFAULT_FONT_FAMILY,

    '&, &:before, &:after': {
      color: '#3BB0FF',
      borderRadius: '0px 2px 2px 0px',
    },

    '&, &:active, &:focus': {
      borderRadius: '0px 2px 2px 0px',
    },
  },

  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    background: '#191C2E',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
    margin: 0,
    borderRadius: '8px',
    fontSize: '16px',
  },

  wrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '8px 8px 8px 16px',
    gap: '4px',
    width: (props: { width: number }) => `${props.width}px`,
    height: '40px',
    background: 'rgba(59, 176, 255, 0.1)',
    border: (props: StyleProps) =>
      (props.isActive ? '1px solid #3BB0FF' : '1px solid rgba(59, 176, 255, 0.1)'),
    borderRadius: '32px',
    flex: 'none',
    order: 0,
    flexGrow: 0,
    position: 'relative',
  },

  title: {
    color: '#fff',
    borderRadius: '2px 0px 0px 2px',
    display: 'inline-block',
    height: '33px',
  },

  option: {
    height: '48px',
    color: '#fff',
    '&.Mui-selected': {
      backgroundColor: 'transparent',
    },
    '&.Mui-selected:hover': {
      backgroundColor: 'transparent',
    },
    '&:first-child': {
      marginTop: '-8px',
    },

    '&:last-child': {
      marginBottom: '-8px',
    },

    '&:hover': {
      opacity: 1,
    },
  },

  selected: {
    color: '#3BB0FF',
    opacity: 1,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: '16px',
    backgroundColor: 'none !imporant',
  },
}));

type Props = {
  options: string[];
  currentOption: string;
  onPeriodChange?: (period: string) => void;
  onTimeChange?: (index: number) => void;
  hasBackground: boolean;
  disabled?: boolean;
  width: number;
}

const FilterDropDown = ({
  options,
  currentOption,
  onPeriodChange,
  onTimeChange,
  hasBackground,
  disabled,
  width,
}: Props) => {
  const [isActive, setIsActive] = useState(false);
  const wrapperRef = useRef(null);
  const classes = useStyles({ width, isActive });
  const { browserWidthSize } = useBrowserWidth();
  const [paperDropDown, setPaperDropDown] = useState<number>(90);

  useEffect(() => {
    setPaperDropDown(90 - Math.floor(window.innerWidth / 10));
  }, [browserWidthSize]);

  const capitalizeFirstLetter = (text: string | undefined) => {
    if (!text) return '';
    return text !== RatiosPeriodType.TTM ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text;
  };

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      <div className={classes.title}>
        <div className={classNames(styles.titleText, disabled ? styles.titleTextDisabled : styles.titleTextEnabled)}>
          {disabled ? lang.commonNA() : capitalizeFirstLetter(currentOption)}
        </div>
      </div>
      <FormControl>
        <Select
          disableUnderline
          disabled={disabled}
          displayEmpty
          value={currentOption}
          onChange={(period) => onPeriodChange && onPeriodChange(period.target.value as string)}
          onOpen={() => setIsActive(true)}
          onClose={() => setIsActive(false)}
          classes={{ select: classes.select }}
          IconComponent={(props) =>
            (<div {...props}><img className={disabled ? styles.iconDisabled : styles.icon} src={iconExpandUp} alt="icon filter" /></div>)}
          MenuProps={{
            anchorEl: wrapperRef.current,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            PaperProps: {
              style: {
                width: 132,
                marginTop: '5px',
                zoom: browserWidthSize <= ZOOM_BEHAVIOR_WIDTH ? `${Math.floor(browserWidthSize / 10).toString()}%` : '',
              },
            },
            classes: {
              paper: classes.paper,
            },
            getContentAnchorEl: null,
          }}
          renderValue={() => null}
        >
          {
            options.map((option, index) => (
              <MenuItem
                key={option}
                onClick={() => onTimeChange && onTimeChange(index)}
                value={option}
                classes={{
                  root: classes.option,
                  selected: classes.selected,
                }}
              >
                <span>{capitalizeFirstLetter(option)}</span>
              </MenuItem>
            ))
          }
        </Select>
      </FormControl>
    </div>
  );
};

export default FilterDropDown;
